import { ChangeEvent, KeyboardEvent, ReactNode, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { Mic, TriangleAlert as ErrorTextIcon } from 'lucide-react';

import { classNameList } from 'utils/property';
import { capitalize } from 'utils/string';
import { useTranslate } from 'hooks/useTranslate';
import { TranslationsKeys } from 'types/translation.ts';
import { Attachment } from 'types/surveyResponse';
import { Audio, AudioHandle } from 'components/Audio';
import { Tooltip } from 'components/ui/Tooltip';
import styles from './TextInput.module.scss';

interface IProps {
  label?: string;
  subLabel?: string;
  hideLabels?: boolean;
  name?: string;
  state?: 'disabled' | 'error';
  size?: 'small' | 'medium';
  text?: string;
  prefixIcon?: ReactNode;
  suffixIcon?: ReactNode;
  showCharacterCount?: boolean;
  maxLength?: number;
  textarea?: boolean;
  errorText?: string;
  inputRef?: Ref<HTMLInputElement | HTMLTextAreaElement>;
  audios?: Attachment[];
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChangeAudio?: (attachments: Attachment[]) => void;
}

export const TextInput = ({
  label,
  subLabel,
  hideLabels,
  name,
  state,
  size = 'medium',
  text = '',
  prefixIcon,
  suffixIcon,
  showCharacterCount = false,
  maxLength,
  textarea,
  errorText,
  inputRef,
  audios,
  onKeyDown,
  onChange,
  onChangeAudio,
}: IProps): JSX.Element => {
  const disabled = state === 'disabled';
  const hasError = state === 'error';
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  if (!inputRef) {
    inputRef = textareaRef;
  }
  const audioHandleRef = useRef<AudioHandle>(null);
  const [showRecordingTooltip, setShowRecordingTooltip] = useState(false);
  const { t } = useTranslate();

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, []);
  
  const onStartRecording = useCallback(() => {
    audioHandleRef.current?.startRecording();
    setShowRecordingTooltip(true);
  }, [audioHandleRef.current]);
  const onStopRecording = useCallback(() => {
    audioHandleRef.current?.stopRecording();
    setShowRecordingTooltip(false);
  }, [audioHandleRef.current]);

  return <>
    <div
      className={classNameList(
        styles.textInput,
        disabled && styles.textInputDisabled,
        hasError && styles.textInputError
      )}>
      {label && !hideLabels && (
        <label htmlFor={name} className={styles.textInputLabel}>
          {label}
        </label>
      )}
      {subLabel && !hideLabels && (
        <label className={styles.textInputSubLabel}>
          {subLabel}
        </label>
      )}
      <div className={styles.textInputWrapper}>
        {prefixIcon && <span className={styles.textInputPrefixIcon}>{prefixIcon}</span>}
        {textarea
          ? <textarea
            ref={inputRef as Ref<HTMLTextAreaElement>}
            id={name}
            name={name}
            value={text}
            className={classNameList(styles.textInputField, styles[`textInputField${capitalize(size)}`])}
            maxLength={maxLength}
            rows={1}
            onInput={() => {
              const textarea = textareaRef.current!;
              textarea.style.height = 'auto';
              textarea.style.height = textarea.scrollHeight + 'px';
            }}
            onKeyDown={onKeyDown}
            onChange={onChange}
            disabled={disabled}
            aria-invalid={hasError}
          />
          :
          <input
            id={name}
            ref={inputRef as Ref<HTMLInputElement>}
            name={name}
            type="text"
            value={text}
            className={classNameList(styles.textInputField, styles[`textInputField${capitalize(size)}`])}
            maxLength={maxLength}
            onKeyDown={onKeyDown}
            onChange={onChange}
            disabled={disabled}
            aria-invalid={hasError}
          />}
        {suffixIcon && <span className={styles.textInputSuffixIcon}>{suffixIcon}</span>}
        {onChangeAudio && <button 
          className={styles.textInputSuffixIcon}
          onMouseDown={onStartRecording}
          onTouchStart={onStartRecording}
          onMouseUp={onStopRecording}
          onTouchEnd={onStopRecording}
          onTouchCancel={onStopRecording}
        ><Mic /></button>}
        {onChangeAudio && <Tooltip show={showRecordingTooltip}>{t(TranslationsKeys.audio_recording)}</Tooltip>}
      </div>
      {hasError && errorText && (
        <p className={styles.textInputErrorText}>
          <ErrorTextIcon/>
          <span>{errorText}</span>
        </p>
      )}
      {showCharacterCount && (
        <p
          className={
            styles.textInputCharCount
          }>{`${text.length}/${typeof maxLength !== 'undefined' ? maxLength : ''}`}</p>
      )}
    </div>
    {onChangeAudio && <Audio
      ref={audioHandleRef}
      label={label ? `${label} - ${t(TranslationsKeys.audio_recordingName)}` : undefined}
      audios={audios}    
      onChange={onChangeAudio} 
    />}
  </>;
};
